import React, { useEffect, useRef, useState } from 'react';
import {
    Archive, ArrowRight, BadgeCheck, Bell, BellRing, Briefcase, Calendar, CalendarCheck, CalendarClock, Check, ChevronDown, ExternalLink,
    FileText, Footprints, Hand, Mail, ScanFace, UserCheck, UserPlus, UserX, Zap, Clock,
} from 'lucide-react';
import { TONES, toneOf } from '../../../lib/eventJourney';
import { formatDayLabel } from '../../../lib/taskDue';

const ICONS = {
    archive: Archive, 'arrow-right': ArrowRight, 'badge-check': BadgeCheck, bell: Bell, 'bell-ring': BellRing, briefcase: Briefcase,
    calendar: CalendarClock, 'calendar-check': CalendarCheck, 'calendar-days': Calendar, check: Check, 'external-link': ExternalLink,
    'file-text': FileText, footprints: Footprints, hand: Hand, mail: Mail, 'scan-face': ScanFace, 'user-check': UserCheck,
    'user-plus': UserPlus, 'user-x': UserX, zap: Zap, clock: Clock,
};

/** A lucide icon by the short names `lib/eventJourney` and `lib/eventActivityLog` use. */
export function GuestIcon({ name, className = 'h-3.5 w-3.5', ...rest }) {
    const Component = ICONS[name];
    return Component ? <Component className={className} aria-hidden="true" {...rest} /> : null;
}

/** A small tinted pill (a fact, a due chip, a reason). */
export function TonePill({ tone = 'stone', icon, children, title, className = '', ...rest }) {
    const t = TONES[tone] || TONES.stone;
    return (
        <span
            title={title}
            className={`inline-flex h-5 max-w-full items-center gap-1 whitespace-nowrap rounded-[5px] border px-1.5 text-[11px] font-medium ${className}`}
            style={{ background: t.bg, color: t.fg, borderColor: t.bd }}
            {...rest}
        >
            {icon && <GuestIcon name={icon} className="h-3 w-3 shrink-0" />}
            <span className="truncate">{children}</span>
        </span>
    );
}

/** The lightning bolt that marks a column which moves cards by itself. */
export function AutoBolt({ title }) {
    return (
        <span title={title} data-testid="column-auto" className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-amber-100 text-amber-700">
            <Zap className="h-3 w-3" aria-label="Moves on its own" />
        </span>
    );
}

export const initialsOf = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || '?';

export const cardSub = (card) => [card?.title, card?.company].filter(Boolean).join(' · ') || 'No title or company';

export function OwnerAvatar({ card }) {
    if (!card?.owner_name) return null;
    return (
        <span title={`Contact Owner · ${card.owner_name}`} className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full border border-stone-200 bg-stone-100 text-[9px] font-semibold text-stone-700">
            {card.owner_avatar_url ? <img src={card.owner_avatar_url} alt="" className="h-full w-full object-cover" /> : initialsOf(card.owner_name)}
        </span>
    );
}

/** A server date (a follow-up due date) as a label: date-only follow-ups keep their calendar day. */
export function shortDate(value, dateOnly = false) {
    if (!value) return '';
    if (dateOnly) {
        const day = formatDayLabel(value);
        if (day) return day;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function StageChip({ column, label, onClick, menuOpen, disabled, ...rest }) {
    const t = toneOf(column.key, [column]);
    const body = (
        <>
            <span className="h-2 w-2 rounded-full" style={{ background: t.dot }} />
            <span>{label}</span>
            {onClick && <ChevronDown className="h-3 w-3" aria-hidden="true" />}
        </>
    );
    const style = { background: t.bg, color: t.fg, borderColor: t.bd };
    const base = 'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md border px-2 text-xs font-medium';
    if (!onClick) return <span className={base} style={style} {...rest}>{body}</span>;
    return <button type="button" disabled={disabled} aria-haspopup="menu" aria-expanded={Boolean(menuOpen)} onClick={onClick} className={`${base} disabled:cursor-not-allowed disabled:opacity-60`} style={style} {...rest}>{body}</button>;
}

/**
 * "Move to…": the keyboard alternative to dragging a card (the issue's a11y rule). A button opens a menu of
 * every column; choosing one ASKS for the move (the caller opens the evidence form). Escape and an outside
 * click close it; the arrow keys walk the items.
 */
export function MoveToMenu({ columns, current, onPick, disabled, trigger, label = 'Move to…', align = 'left', direction = 'down', testId = 'move-to-menu' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);
    const onKeyDown = (event) => {
        if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); return; }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const items = Array.from(ref.current?.querySelectorAll('[role="menuitem"]') || []);
        if (!items.length) return;
        event.preventDefault();
        const index = items.indexOf(document.activeElement);
        items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus();
    };
    return (
        <span ref={ref} className="relative inline-block" onKeyDown={onKeyDown}>
            {trigger ? trigger({ open, toggle: () => setOpen((value) => !value), disabled }) : (
                <button type="button" disabled={disabled} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((value) => !value)}
                    className="inline-flex h-7 items-center gap-1 rounded-md border border-stone-200 bg-white px-2 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                    {label}<ChevronDown className="h-3 w-3" aria-hidden="true" />
                </button>
            )}
            {open && (
                <ul role="menu" aria-label={label} data-testid={testId}
                    className={`absolute z-[70] w-56 rounded-lg border border-stone-200 bg-white p-1 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'} ${direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                    {columns.map((column) => {
                        const t = toneOf(column.key, columns);
                        const here = column.key === current;
                        return (
                            <li key={column.key} role="none">
                                <button type="button" role="menuitem" data-testid={`${testId}-${column.key}`} disabled={here}
                                    onClick={() => { setOpen(false); onPick(column.key); }}
                                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-stone-100 disabled:opacity-60">
                                    <span className="h-2 w-2 rounded-full" style={{ background: t.dot }} />
                                    <span className="flex-1">{column.label}</span>
                                    {column.auto && <Zap className="h-3 w-3 text-amber-700" aria-label="Moves on its own" />}
                                    {here && <Check className="h-3 w-3" aria-label="Current column" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </span>
    );
}
